<template>
  <div class="kreativ-board-container pa-0">
    <div class="d-flex flex-wrap align-center justify-space-between mb-6 px-4 px-md-6 gap-3">
      <div class="d-flex align-center">
        <v-btn icon dark class="mr-3" @click="$router.push('/akademie/kreativwerkstatt')">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <div class="text-caption gold-text text-uppercase">Kategorie</div>
          <h2 class="text-h5 text-md-h4 font-weight-bold white--text">{{ categoryTitle }}</h2>
        </div>
      </div>

      <v-btn color="#D4AF37" class="black--text font-weight-bold" rounded @click="showCreateDialog = true">
        <v-icon left>mdi-plus</v-icon> Idee teilen
      </v-btn>
    </div>

    <div class="masonry-grid px-4 px-md-6">
      <idea-card v-for="idea in ideas" :key="idea._id" :idea="idea" @click="openIdea(idea)" @like="toggleLike"></idea-card>
    </div>

    <v-row v-if="ideas.length === 0 && !loading" class="mt-8">
      <v-col class="text-center py-12">
        <v-icon size="64" color="grey darken-3">mdi-message-draw</v-icon>
        <h3 class="text-h6 grey--text mt-4">Noch keine Ideen in dieser Kategorie</h3>
        <p class="text-caption grey--text max-w-400 mx-auto mt-2">Sei die erste Person, die ihre Best Practice teilt!</p>
      </v-col>
    </v-row>

    <!-- Detail Dialog -->
    <v-dialog v-model="detailDialog" max-width="780" dark scrollable>
      <v-card v-if="activeIdea" style="background: #1e1e1e; border-radius: 18px">
        <v-card-title class="d-flex align-center">
          <v-avatar size="36" color="grey darken-3" class="mr-3">
            <span class="white--text">{{ initials(activeIdea.authorName) }}</span>
          </v-avatar>
          <div>
            <div class="font-weight-bold">{{ activeIdea.authorName }}</div>
            <div class="text-caption grey--text">{{ formatDateTime(activeIdea.createdAt) }}</div>
          </div>
          <v-spacer />
          <v-btn icon @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text style="max-height: 75vh">
          <h2 class="text-h5 font-weight-bold white--text mb-3">{{ activeIdea.title }}</h2>
          <p class="text-body-1 grey--text text--lighten-2 mb-4" style="white-space: pre-wrap">{{ activeIdea.description }}</p>

          <!-- Attachments -->
          <div v-if="activeIdea.attachments && activeIdea.attachments.length" class="attachments-grid mb-4">
            <div v-for="att in activeIdea.attachments" :key="att.fileId" class="attachment-item">
              <SecuredMedia
                :src="attachmentPath(activeIdea._id, att.fileId)"
                :media-type="attachmentType(att)"
                :download-name="att.originalName"
                eager />
            </div>
          </div>

          <v-divider class="my-4 border-white-05"></v-divider>

          <!-- Like -->
          <div class="d-flex align-center mb-4">
            <v-btn rounded :color="activeIdea.likedByMe ? 'red' : 'grey darken-3'" text @click="toggleLike(activeIdea._id)">
              <v-icon left>{{ activeIdea.likedByMe ? "mdi-heart" : "mdi-heart-outline" }}</v-icon>
              {{ activeIdea.likeCount || 0 }} Likes
            </v-btn>
            <v-spacer />
            <span class="grey--text text-caption">
              <v-icon small class="mr-1">mdi-comment-outline</v-icon>
              {{ activeIdea.comments ? activeIdea.comments.length : 0 }} Kommentare
            </span>
          </div>

          <!-- Comments -->
          <h4 class="text-subtitle-1 white--text mb-3">Kommentare</h4>
          <div class="comments-list mb-3">
            <div v-for="c in activeIdea.comments" :key="c._id" class="comment-item d-flex pa-2 rounded-lg mb-2">
              <v-avatar size="28" color="grey darken-3" class="mr-2">
                <span class="text-caption white--text">{{ initials(c.authorName) }}</span>
              </v-avatar>
              <div class="flex-grow-1" style="min-width: 0">
                <div class="text-caption">
                  <span class="white--text font-weight-bold">{{ c.authorName }}</span>
                  <span class="grey--text ml-2">{{ formatDateTime(c.createdAt) }}</span>
                </div>
                <div class="text-body-2 grey--text text--lighten-2" style="white-space: pre-wrap">{{ c.text }}</div>
              </div>
              <v-btn v-if="canDeleteComment(c)" icon x-small color="grey" @click="deleteComment(c)">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
            <div v-if="!activeIdea.comments || !activeIdea.comments.length" class="text-caption grey--text text-center pa-3">
              Noch keine Kommentare. Sei der/die Erste!
            </div>
          </div>

          <div class="d-flex align-end">
            <v-textarea
              v-model="newComment"
              placeholder="Schreib einen Kommentar..."
              outlined
              dense
              rows="2"
              auto-grow
              hide-details
              color="#D4AF37"
              class="flex-grow-1 mr-2"></v-textarea>
            <v-btn color="#D4AF37" class="black--text" icon large :disabled="!newComment.trim()" :loading="postingComment" @click="postComment">
              <v-icon>mdi-send</v-icon>
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Create Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="600" dark>
      <v-card class="rounded-xl" style="background: #1e1e1e">
        <v-card-title class="text-h5 font-weight-bold pt-6 px-6">Neue Idee teilen</v-card-title>
        <v-card-text class="px-6 pt-4">
          <v-text-field v-model="newIdea.title" label="Titel" outlined color="#D4AF37" dense></v-text-field>
          <v-textarea v-model="newIdea.description" label="Beschreibung / Praxis-Beispiel" outlined color="#D4AF37" rows="4"></v-textarea>
          <v-file-input v-model="newIdea.files" label="Anhänge (Bild, Video, …)" outlined dense multiple chips prepend-icon="mdi-paperclip"></v-file-input>
        </v-card-text>
        <v-card-actions class="px-6 pb-6">
          <v-spacer></v-spacer>
          <v-btn text color="grey" rounded @click="showCreateDialog = false">Abbrechen</v-btn>
          <v-btn color="#D4AF37" class="black--text font-weight-bold px-6 ml-2" rounded @click="submitIdea" :loading="submitting" :disabled="!newIdea.title || !newIdea.description">
            Teilen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" top right>
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import IdeaCard from "./IdeaCard.vue"
import SecuredMedia from "./SecuredMedia.vue"
import api from "@/api"

export default {
  name: "KreativBoard",
  components: { IdeaCard, SecuredMedia },
  data() {
    return {
      categoryTitle: "Lade Kategorie...",
      ideas: [],
      showCreateDialog: false,
      detailDialog: false,
      activeIdea: null,
      newComment: "",
      postingComment: false,
      newIdea: { title: "", description: "", files: [] },
      submitting: false,
      loading: false,
      snackbar: { show: false, color: "success", text: "" },
    }
  },
  computed: {
    me() {
      try {
        return JSON.parse(localStorage.getItem("user") || "{}")
      } catch (_) {
        return {}
      }
    },
  },
  mounted() {
    this.loadIdeas()
    this.loadModuleInfo()
  },
  methods: {
    notify(color, text) {
      this.snackbar = { show: true, color, text }
    },
    initials(name) {
      if (!name) return "?"
      const parts = name.trim().split(/\s+/)
      return ((parts[0] || "?")[0] + (parts[1] ? parts[1][0] : "")).toUpperCase()
    },
    formatDateTime(date) {
      if (!date) return ""
      const d = new Date(date)
      return d.toLocaleDateString("de-CH") + " " + d.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })
    },
    async loadModuleInfo() {
      try {
        const mod = await api.akademie.fetchModule(this.$route.params.id)
        if (mod && mod.title) this.categoryTitle = mod.title
      } catch (e) {
        /* ignore */
      }
    },
    async loadIdeas() {
      this.loading = true
      try {
        this.ideas = (await api.akademie.fetchIdeas(this.$route.params.id)) || []
      } catch (err) {
        console.error("Fehler beim Laden der Ideen:", err)
      } finally {
        this.loading = false
      }
    },
    async openIdea(idea) {
      this.detailDialog = true
      this.activeIdea = idea
      // Vollständige Daten inkl. Kommentare nachladen
      try {
        const full = await api.akademie.fetchIdea(idea._id)
        this.activeIdea = full
      } catch (e) {
        console.error(e)
      }
    },
    attachmentPath(ideaId, fileId) {
      return api.akademie.getIdeaAttachmentPath(ideaId, fileId)
    },
    attachmentType(att) {
      if (!att || !att.mimeType) return "file"
      if (att.mimeType.startsWith("image/")) return "image"
      if (att.mimeType.startsWith("video/")) return "video"
      if (att.mimeType.startsWith("audio/")) return "audio"
      if (att.mimeType === "application/pdf") return "pdf"
      return "file"
    },
    async toggleLike(ideaId) {
      try {
        const res = await api.akademie.toggleLike(ideaId)
        // In Liste updaten
        const ideaInList = this.ideas.find((i) => i._id === ideaId)
        if (ideaInList) {
          this.$set(ideaInList, "likedByMe", res.likedByMe)
          this.$set(ideaInList, "likeCount", res.likeCount)
        }
        if (this.activeIdea && this.activeIdea._id === ideaId) {
          this.$set(this.activeIdea, "likedByMe", res.likedByMe)
          this.$set(this.activeIdea, "likeCount", res.likeCount)
        }
      } catch (err) {
        this.notify("error", "Liken fehlgeschlagen")
      }
    },
    async postComment() {
      if (!this.newComment.trim() || !this.activeIdea) return
      this.postingComment = true
      try {
        const c = await api.akademie.addComment(this.activeIdea._id, this.newComment.trim())
        if (!this.activeIdea.comments) this.$set(this.activeIdea, "comments", [])
        this.activeIdea.comments.push(c)
        const ideaInList = this.ideas.find((i) => i._id === this.activeIdea._id)
        if (ideaInList) this.$set(ideaInList, "commentCount", (ideaInList.commentCount || 0) + 1)
        this.newComment = ""
      } catch (err) {
        this.notify("error", "Kommentar konnte nicht gesendet werden")
      } finally {
        this.postingComment = false
      }
    },
    canDeleteComment(c) {
      return this.me && c.authorId && c.authorId.toString() === (this.me._id || "").toString()
    },
    async deleteComment(c) {
      if (!confirm("Kommentar löschen?")) return
      try {
        await api.akademie.deleteComment(this.activeIdea._id, c._id)
        this.activeIdea.comments = this.activeIdea.comments.filter((x) => x._id !== c._id)
        const ideaInList = this.ideas.find((i) => i._id === this.activeIdea._id)
        if (ideaInList && ideaInList.commentCount > 0) ideaInList.commentCount -= 1
      } catch (e) {
        this.notify("error", "Löschen fehlgeschlagen")
      }
    },
    async submitIdea() {
      if (!this.newIdea.title || !this.newIdea.description) return
      this.submitting = true
      try {
        const formData = new FormData()
        formData.append("title", this.newIdea.title)
        formData.append("description", this.newIdea.description)
        formData.append("module", this.$route.params.id)
        if (this.newIdea.files && this.newIdea.files.length) {
          this.newIdea.files.forEach((f) => formData.append("files", f))
        }
        await api.akademie.createIdea(formData)
        this.showCreateDialog = false
        this.newIdea = { title: "", description: "", files: [] }
        this.loadIdeas()
        this.notify("success", "Idee geteilt")
      } catch (err) {
        this.notify("error", "Idee konnte nicht erstellt werden")
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.kreativ-board-container {
  min-height: 100vh;
  background-color: #121212;
  padding-bottom: 40px;
}
.gold-text { color: #d4af37 !important; }
.gap-3 { gap: 12px; }
.max-w-400 { max-width: 400px; }

.masonry-grid {
  column-count: 1;
  column-gap: 20px;
}
@media (min-width: 600px) { .masonry-grid { column-count: 2; } }
@media (min-width: 960px) { .masonry-grid { column-count: 3; } }
@media (min-width: 1264px) { .masonry-grid { column-count: 4; } }

.border-white-05 { border-color: rgba(255, 255, 255, 0.05) !important; }

.attachments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.comment-item {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.04);
}
</style>
